import type { IFacilitatorSuggestion, ISpeakerHypothesisSnapshot, TCoachTone } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Button, Group, Modal, Text } from "@mantine/core"
import { useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useState } from "react"
import type {
  ILanguageSignal,
  ILanguageSuggestion,
  IProgressiveSummary,
  IStreamingSegment,
} from "../../hooks/useStreamingTranscript"
import { FinalizationProgress, type TProcessingStage } from "../FinalizationProgress"
import { CaptureMonitor } from "./CaptureMonitor"
import { CoachCard } from "./CoachCard"
import type { TConnectionHealth } from "./ConnectionPill"
import { InsightsCard, InsightsFab } from "./InsightsCard"
import type { IAudioPipelineHandle } from "./interfaces"
import { LanguagePill } from "./LanguagePill"
import { LanguageSwitchPrompt } from "./LanguageSwitchPrompt"
import { RecordingBar } from "./RecordingBar"
import classes from "./RecordingFocus.module.css"
import { SpeakerHypothesisStrip } from "./SpeakerHypothesisStrip"
import { Topbar } from "./Topbar"
import { TranscriptCard } from "./TranscriptCard"
import type { TMicLevel } from "./Visualizer"

interface IProps {
  /** True while the recording is paused. Mutually exclusive with `finalizing`. */
  paused: boolean
  /**
   * True after the user has clicked Finish — recording is over and the analysis
   * pipeline is running. Hides the recording bar, shows the FinalizationProgress
   * stepper, and freezes the transcript / insights as read-only.
   */
  finalizing?: boolean
  /** Offline → audio is captured + held locally; live transcript/insights pause until reconnect. */
  offlineRecordingOnly?: boolean
  /**
   * Recoverable error message (finalize failure, upload failure, etc.). null/empty
   * = no banner. Rendered as a dismissible danger Alert at the top of the surface.
   */
  error?: string | null
  /** Dismisses the error banner. */
  onDismissError?: () => void
  /** True while the live-transcription WebSocket is in its reconnect loop. */
  wsReconnecting?: boolean
  /** Aggregate recording-health status — drives the topbar connection chip. */
  health?: TConnectionHealth
  /** Outstanding health issues — surfaced in the connection chip's popover. */
  healthIssues?: string[]
  /** Current stage of the finalize pipeline (drives the FinalizationProgress UI). */
  processingStage?: TProcessingStage | null
  subject: string
  startTime: string | null
  elapsed: number
  level: TMicLevel
  audio: IAudioPipelineHandle
  segments: IStreamingSegment[]
  interimText: string
  interimSpeaker: string
  /**
   * Seconds of speech with no interim or final text. The capture card turns
   * amber once this reaches the unanswered-speech threshold, including before
   * the first word.
   */
  transcriptStaleSec?: number
  speakerColors?: Map<string, string>
  progressiveSummary: IProgressiveSummary | null
  /** Number of progressive_summary events actually received (not a
   *  time-based estimate). 0 means the live-insights pipeline is silent —
   *  rendered as a "waiting…" hint instead of a counter so users can
   *  distinguish "no updates yet" from "100 updates received". */
  progressiveSummaryCount?: number
  /** ISO timestamp of the most recent progressive_summary event. */
  progressiveSummaryAt?: string | null
  onPause: () => void
  onResume: () => void
  onFinish: () => void
  meetingId?: string
  onRename?: () => void
  // Facilitator coach ("Worth asking"). Optional so finalize + stories can omit;
  // the live wrapper always supplies them.
  coachEnabled?: boolean
  coachTone?: TCoachTone
  coachSuggestion?: IFacilitatorSuggestion | null
  coachHistory?: IFacilitatorSuggestion[]
  coachAsking?: boolean
  onCoachToggle?: (enabled: boolean) => void
  onCoachToneChange?: (tone: TCoachTone) => void
  onCoachAsk?: () => void
  // Live speaker-recognition hypotheses (the confirm/reject pills).
  speakerHypotheses?: ISpeakerHypothesisSnapshot[]
  hypothesisPending?: Set<string>
  onHypothesisAction?: (label: string, name: string, action: "confirm" | "reject") => void
  /** Resolved current meeting language (committed switch, signal current, or dialog first). */
  languageCode?: string
  /** Live language-switch progress from the recording socket. */
  languageSignal?: ILanguageSignal | null
  /** True when `languageCode` matches the workspace default meeting language. */
  languageIsWorkspaceDefault?: boolean
  /** One-shot "this sounds like X" prompt. Null when there is nothing to answer. */
  languageSuggestion?: ILanguageSuggestion | null
  /** Switch the meeting to the suggested language. */
  onAcceptLanguageSwitch?: () => void
  /** Keep the current language for this recording. */
  onKeepCurrentLanguage?: () => void
}

const INSIGHTS_PREF_KEY = "lex:recording-insights-shown"
/** Stable empty set so the hypothesis-strip default prop doesn't churn identity. */
const EMPTY_PENDING: Set<string> = new Set()

function readInsightsPref(): boolean {
  if (typeof window === "undefined") return true
  const stored = localStorage.getItem(INSIGHTS_PREF_KEY)
  if (stored === null) return true
  return stored === "true"
}

export function RecordingFocus({
  paused,
  finalizing = false,
  offlineRecordingOnly = false,
  error = null,
  onDismissError,
  wsReconnecting = false,
  health = "nominal",
  healthIssues = [],
  processingStage,
  subject,
  startTime,
  elapsed,
  level,
  audio,
  segments,
  interimText,
  interimSpeaker,
  transcriptStaleSec = 0,
  speakerColors,
  progressiveSummary,
  progressiveSummaryCount = 0,
  progressiveSummaryAt = null,
  onPause,
  onResume,
  onFinish,
  meetingId,
  onRename,
  coachEnabled = true,
  coachTone = "balanced",
  coachSuggestion = null,
  coachHistory = [],
  coachAsking = false,
  onCoachToggle,
  onCoachToneChange,
  onCoachAsk,
  speakerHypotheses = [],
  hypothesisPending = EMPTY_PENDING,
  onHypothesisAction,
  languageCode,
  languageSignal = null,
  languageIsWorkspaceDefault = false,
  languageSuggestion = null,
  onAcceptLanguageSwitch,
  onKeepCurrentLanguage,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  const navigate = useNavigate()
  const [showInsights, setShowInsights] = useState<boolean>(() => readInsightsPref())
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined") return
    localStorage.setItem(INSIGHTS_PREF_KEY, String(showInsights))
  }, [showInsights])

  const handleExitRequest = useCallback(() => {
    setExitConfirmOpen(true)
  }, [])

  const handleExitConfirm = useCallback((): void => {
    setExitConfirmOpen(false)
    navigate({ to: "/" }).catch(() => undefined)
  }, [navigate])

  // Distinct speakers seen so far (finalised + the in-flight interim speaker) —
  // the capture monitor's reassurance count.
  const speakerCount = useMemo(() => {
    const set = new Set<string>()
    for (const seg of segments) set.add(seg.speaker)
    if (interimSpeaker) set.add(interimSpeaker)
    return set.size
  }, [segments, interimSpeaker])

  // The single most recent finalised line — the faint capture ticker.
  const lastLine = useMemo(() => {
    if (segments.length === 0) return null
    const last = segments[segments.length - 1]
    return { speaker: last.speaker, text: last.text }
  }, [segments])

  // Keyboard shortcuts during the recording focus mode:
  //   Space → toggle pause/resume (when not focused inside an input)
  //   Esc → ask to exit focus
  // Both are suppressed while finalizing — the recording is over and there's
  // nothing useful to pause/resume.
  useEffect(() => {
    if (finalizing) return
    const handler = (e: KeyboardEvent): void => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (e.target instanceof HTMLElement && e.target.isContentEditable) return

      if (e.code === "Space" || e.key === " ") {
        // Don't intercept when user is interacting with a button — Space already
        // activates buttons and we don't want a double-fire.
        if (e.target instanceof HTMLButtonElement) return
        e.preventDefault()
        if (paused) onResume()
        else onPause()
      } else if (e.key === "Escape") {
        e.preventDefault()
        setExitConfirmOpen(true)
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [paused, finalizing, onPause, onResume])

  const topbarPhase = finalizing ? "finalizing" : paused ? "paused" : "recording"

  const languagePill = (
    <LanguagePill
      languageCode={languageCode}
      languageSignal={languageSignal}
      languageIsWorkspaceDefault={languageIsWorkspaceDefault}
    />
  )

  return (
    <div className={classes.shell} data-testid="recording-focus">
      <Topbar
        phase={topbarPhase}
        subject={subject}
        startTime={startTime}
        onRename={onRename}
        onExit={handleExitRequest}
        health={health}
        wsReconnecting={wsReconnecting}
        healthIssues={healthIssues}
      />

      {error ? (
        <Alert
          color="danger"
          variant="light"
          title={<Trans>Something went wrong</Trans>}
          withCloseButton={Boolean(onDismissError)}
          onClose={onDismissError}
          mb="md"
          data-testid="recording-error"
        >
          <Text size="sm" mb={4}>
            {error}
          </Text>
          <Text size="xs" c="dimmed">
            <Trans>Your captured audio is safe — nothing was lost.</Trans>
          </Text>
        </Alert>
      ) : null}

      {languageSuggestion && onAcceptLanguageSwitch && onKeepCurrentLanguage ? (
        <LanguageSwitchPrompt
          currentCode={languageSuggestion.current}
          candidateCode={languageSuggestion.candidate}
          onSwitch={onAcceptLanguageSwitch}
          onKeep={onKeepCurrentLanguage}
        />
      ) : null}

      <div className={classes.cockpit}>
        {finalizing ? (
          <div className={classes.finalizeCard}>
            <FinalizationProgress stage={processingStage ?? null} />
          </div>
        ) : (
          <RecordingBar
            paused={paused}
            elapsed={elapsed}
            level={level}
            audio={audio}
            onPause={onPause}
            onResume={onResume}
            onFinish={onFinish}
            meetingId={meetingId}
            languagePill={languagePill}
          />
        )}

        {finalizing ? (
          // Recording is over — keep the verbatim transcript visible (read-only)
          // alongside the frozen insights while the pipeline finishes.
          <div className={classes.bodyGrid} data-insights={showInsights ? "true" : "false"}>
            <TranscriptCard
              segments={segments}
              interimText={interimText}
              interimSpeaker={interimSpeaker}
              paused
              speakerColors={speakerColors}
              micClipping={false}
              readOnly
            />
            {showInsights && (
              <InsightsCard
                progressiveSummary={progressiveSummary}
                updateCount={progressiveSummaryCount}
                lastUpdateAt={progressiveSummaryAt}
                onCollapse={() => setShowInsights(false)}
              />
            )}
          </div>
        ) : (
          // Live recording — two columns on desktop: speakers + capture on the
          // left, the coach + live insights on the right (stacks on mobile). The
          // verbatim transcript starts open inside the capture monitor.
          <div className={classes.liveBody}>
            <div className={classes.col}>
              <SpeakerHypothesisStrip
                hypotheses={speakerHypotheses}
                pending={hypothesisPending}
                onAction={onHypothesisAction ?? (() => undefined)}
              />
              <CaptureMonitor
                level={level}
                volume={audio.volume}
                spectrum={audio.spectrum}
                monitoring={audio.monitoring}
                speakerCount={speakerCount}
                lastLine={lastLine}
                secondsSinceLastWord={transcriptStaleSec}
                paused={paused}
                offlineRecordingOnly={offlineRecordingOnly}
                wsReconnecting={wsReconnecting}
                transcript={
                  <TranscriptCard
                    segments={segments}
                    interimText={interimText}
                    interimSpeaker={interimSpeaker}
                    paused={paused}
                    speakerColors={speakerColors}
                    micClipping={level === "clipping"}
                    embedded
                  />
                }
              />
            </div>
            <div className={classes.col}>
              <CoachCard
                enabled={coachEnabled}
                tone={coachTone}
                suggestion={coachSuggestion}
                history={coachHistory}
                asking={coachAsking}
                onToggle={onCoachToggle ?? (() => undefined)}
                onToneChange={onCoachToneChange ?? (() => undefined)}
                onAsk={onCoachAsk ?? (() => undefined)}
              />
              {showInsights && (
                <InsightsCard
                  progressiveSummary={progressiveSummary}
                  updateCount={progressiveSummaryCount}
                  lastUpdateAt={progressiveSummaryAt}
                  onCollapse={() => setShowInsights(false)}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {!showInsights && <InsightsFab onShow={() => setShowInsights(true)} />}

      <Modal
        opened={exitConfirmOpen}
        onClose={() => setExitConfirmOpen(false)}
        title={i18n._(msg`Leave the recording?`)}
        centered
      >
        <Text size="sm" c="dimmed" mb="md">
          <Trans>
            The recording will keep running in this tab as long as you stay on the page. Leave to come back to the
            meeting list — your audio is still saving.
          </Trans>
        </Text>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={() => setExitConfirmOpen(false)}>
            <Trans>Stay here</Trans>
          </Button>
          <Button color="danger" onClick={handleExitConfirm}>
            <Trans>Leave anyway</Trans>
          </Button>
        </Group>
      </Modal>
    </div>
  )
}
